// The one error-toast idiom for API failures. Both rejection paths — a
// `success:false` envelope on a 2xx and an HTTP/transport failure — reach a
// component's `catch` as the same ApiErrorShape (RTK Query's unwrap() throws
// the base query's error object), so one helper covers both. api/ passes an
// HTTP-error body's `error` through as `msg` (a string or a string ARRAY
// verbatim, an object's `msg`; the 2xx envelope path carries objects only)
// and fills a placeholder on every other path; this file owns
// the display policy only: the 4xx gate keeps placeholders and transport text
// off the toast, and an array shows its FIRST item. A screen that wants the
// whole list (or per-field mapping) reads `err.msg` in its own catch instead.
//
// `t` is a parameter on purpose: importing `@/i18n/config` here would
// self-initialise i18n and flip every component test that reaches this file
// from raw keys to English (see CLAUDE.md convention 9).

import toast from 'react-hot-toast';
import type { TFunction } from 'i18next';

const nonBlank = (v: unknown): string | undefined =>
  typeof v === 'string' && v.trim() ? v : undefined;

/** The wire `msg` as toast text: the string itself, or the first item of a
 *  string array. Read as `unknown` because ApiErrorShape types it `string`
 *  for the older `?.msg ?? fallback` sites while an array can arrive. */
const messageText = (msg: unknown): string | undefined =>
  Array.isArray(msg) ? nonBlank(msg[0]) : nonBlank(msg);

/** The backend's own message when the rejection is a 4xx that carried one
 *  (a string, or a string array whose first item is shown), else undefined.
 *  5xx, envelope rejections on a 2xx, network and decrypt failures never
 *  surface wire text (user decision 2026-10-21). */
export const apiErrorMessage = (err: unknown): string | undefined => {
  const { status, msg } = (err as { status?: unknown; msg?: unknown } | null | undefined) ?? {};
  const clientError = typeof status === 'number' && status >= 400 && status < 500;
  return clientError ? messageText(msg) : undefined;
};

/** Error toast: the server's 4xx message when it sent one, else the generic copy. */
export const toastApiError = (err: unknown, t: TFunction, fallbackKey = 'error_generic') =>
  toast.error(apiErrorMessage(err) ?? t(fallbackKey));
